import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  UserPlus, Edit2, Trash2, X, AlertCircle, Search,
  CheckCircle, XCircle, Phone, Briefcase, Users, IndianRupee,
} from 'lucide-react';
import { employeesAPI, employeeAttendanceAPI, salaryAPI } from '../services/api';

// ── Constants (same lists as Biometric staff for consistency) ─────────────────
const DESIGNATIONS = [
  'Principal', 'Vice Principal', 'Head Master', 'Head Mistress',
  'Senior Teacher', 'Teacher', 'Assistant Teacher', 'PET Teacher',
  'Lab Assistant', 'Librarian', 'Clerk', 'Accountant',
  'Peon / Attender', 'Security Guard', 'Cleaning Staff',
  'Primary In-charge', 'Pre-primary Incharge', 'Academic Co-ordinator',
  'Kharadi Path Incharge', 'Bus Incharge',
  'Other',
];

const DEPARTMENTS = [
  'Administration', 'Teaching Staff', 'Science Dept', 'Maths Dept',
  'Social Studies Dept', 'Language Dept', 'Physical Education',
  'Library', 'Accounts', 'Support Staff',
  'English Dept', 'Hindi Dept', 'Telugu', 'Kharadi Path Dept',
  'Bio-science Dept', 'EVS',
];

const fmt = (n) =>
  n != null
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)
    : '—';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const EMPTY_FORM = {
  full_name: '', designation: '', department: '', phone_number: '',
  emp_code: '', salary: '', join_date: '', address: '', notes: '', is_active: true,
};

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ active }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11,
      fontWeight: 600, padding: '2px 8px', borderRadius: 12,
      background: active ? '#d1fae5' : '#fee2e2',
      color: active ? '#065f46' : '#991b1b',
    }}>
      {active ? <CheckCircle size={10} /> : <XCircle size={10} />}
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

// ── Add / Edit Modal ──────────────────────────────────────────────────────────
function EmployeeModal({ employee, onClose, onSaved }) {
  const isEdit = !!employee?.employee_id;
  const [form, setForm] = useState(
    isEdit
      ? {
          full_name:    employee.full_name    || '',
          designation:  employee.designation  || '',
          department:   employee.department   || '',
          phone_number: employee.phone_number || '',
          emp_code:     employee.emp_code     || '',
          salary:       employee.salary != null ? String(employee.salary) : '',
          join_date:    employee.join_date    || '',
          address:      employee.address      || '',
          notes:        employee.notes        || '',
          is_active:    employee.is_active ?? true,
        }
      : { ...EMPTY_FORM }
  );
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.full_name.trim()) { setErr('Full name is required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = {
        ...form,
        salary:    form.salary    !== '' ? parseFloat(form.salary)    : null,
        join_date: form.join_date !== '' ? form.join_date             : null,
      };
      if (isEdit) {
        await employeesAPI.update(employee.employee_id, payload);
      } else {
        await employeesAPI.create(payload);
      }
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 7,
    border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none',
    boxSizing: 'border-box', fontFamily: 'inherit', color: '#1e293b',
  };
  const labelStyle = {
    fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block',
  };

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560,
        maxHeight: '92vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 22px', borderBottom: '1px solid #f1f5f9',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: '#4f46e5',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Users size={18} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                {isEdit ? 'Edit Employee' : 'Add Employee'}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                {isEdit ? 'Update employee details' : 'Register a new employee'}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={18} color="#94a3b8" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 22px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

          {/* Full Name — full width */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
            <input style={inputStyle} value={form.full_name}
              onChange={(e) => set('full_name', e.target.value)}
              placeholder="e.g. Ramesh Kumar" />
          </div>

          {/* Designation */}
          <div>
            <label style={labelStyle}>Designation</label>
            <select style={inputStyle} value={form.designation}
              onChange={(e) => set('designation', e.target.value)}>
              <option value="">-- Select --</option>
              {DESIGNATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          {/* Department */}
          <div>
            <label style={labelStyle}>Department</label>
            <select style={inputStyle} value={form.department}
              onChange={(e) => set('department', e.target.value)}>
              <option value="">-- Select --</option>
              {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          {/* Phone */}
          <div>
            <label style={labelStyle}>Phone Number</label>
            <input style={inputStyle} value={form.phone_number}
              onChange={(e) => set('phone_number', e.target.value.replace(/[^\d]/g, ''))}
              placeholder="10-digit mobile" maxLength={15} />
          </div>

          {/* Employee Code */}
          <div>
            <label style={labelStyle}>Employee Code</label>
            <input style={inputStyle} value={form.emp_code}
              onChange={(e) => set('emp_code', e.target.value)}
              placeholder="e.g. EMP-001" />
          </div>

          {/* Salary */}
          <div>
            <label style={labelStyle}>Monthly Salary (₹)</label>
            <input style={inputStyle} type="number" min="0" step="1" value={form.salary}
              onChange={(e) => set('salary', e.target.value)}
              placeholder="e.g. 25000" />
          </div>

          {/* Join Date */}
          <div>
            <label style={labelStyle}>Date of Joining</label>
            <input style={inputStyle} type="date" value={form.join_date} lang="en-GB"
              onChange={(e) => set('join_date', e.target.value)} />
          </div>

          {/* Address — full width */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Address</label>
            <input style={inputStyle} value={form.address}
              onChange={(e) => set('address', e.target.value)}
              placeholder="Residential address" />
          </div>

          {/* Notes — full width */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Notes</label>
            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Any additional information…" />
          </div>

          {/* Active toggle — only on edit */}
          {isEdit && (
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" id="is_active" checked={!!form.is_active}
                onChange={(e) => set('is_active', e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }} />
              <label htmlFor="is_active" style={{ ...labelStyle, margin: 0, cursor: 'pointer' }}>
                Active Employee
              </label>
            </div>
          )}
        </div>

        {/* Error */}
        {err && (
          <div style={{
            margin: '0 22px 14px', padding: '10px 14px',
            background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca',
            color: '#dc2626', fontSize: 13, display: 'flex', gap: 8,
          }}>
            <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            {err}
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '14px 22px', borderTop: '1px solid #f1f5f9',
          display: 'flex', gap: 10, justifyContent: 'flex-end',
          position: 'sticky', bottom: 0, background: '#fff',
        }}>
          <button onClick={onClose} style={{
            padding: '8px 18px', borderRadius: 8, border: '1.5px solid #e2e8f0',
            background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#64748b',
          }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '8px 22px', borderRadius: 8, border: 'none',
            background: '#4f46e5', color: '#fff',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: 13, fontWeight: 600, opacity: saving ? 0.7 : 1,
          }}>
            {saving ? 'Saving…' : (isEdit ? 'Update Employee' : 'Add Employee')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Employee List Panel ────────────────────────────────────────────────────────
function EmployeeListPanel() {
  const [employees,   setEmployees]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [search,      setSearch]      = useState('');
  const [filterDept,  setFilterDept]  = useState('');
  const [filterStatus, setFilterStatus] = useState('active'); // 'all' | 'active' | 'inactive'
  const [modalOpen,   setModalOpen]   = useState(false);
  const [editTarget,  setEditTarget]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await employeesAPI.getAll();
      setEmployees(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (emp) => { setEditTarget(emp); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditTarget(null); };
  const onSaved = () => { closeModal(); load(); };

  const handleDelete = async (emp) => {
    if (!window.confirm(`Remove ${emp.full_name} from the employee registry?`)) return;
    try {
      await employeesAPI.delete(emp.employee_id);
      load();
    } catch {
      alert('Failed to delete employee.');
    }
  };

  // ── Derived data ──────────────────────────────────────────────────────────
  const filtered = employees.filter((e) => {
    const matchSearch =
      e.full_name.toLowerCase().includes(search.toLowerCase()) ||
      (e.emp_code || '').toLowerCase().includes(search.toLowerCase()) ||
      (e.phone_number || '').includes(search) ||
      (e.designation || '').toLowerCase().includes(search.toLowerCase());
    const matchDept   = !filterDept   || e.department   === filterDept;
    const matchStatus =
      filterStatus === 'all'      ? true :
      filterStatus === 'active'   ? !!e.is_active :
                                    !e.is_active;
    return matchSearch && matchDept && matchStatus;
  });

  const activeCount   = employees.filter((e) => e.is_active).length;
  const inactiveCount = employees.length - activeCount;
  const totalSalary   = employees
    .filter((e) => e.is_active && e.salary != null)
    .reduce((s, e) => s + parseFloat(e.salary), 0);

  const depts = [...new Set(employees.map((e) => e.department).filter(Boolean))].sort();

  return (
    <div className="page-content">
      {/* ── Header ── */}
      <div className="page-header">
        <h1 className="page-title">Employees</h1>
        <button className="btn btn-primary" onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <UserPlus size={15} /> Add Employee
        </button>
      </div>

      {/* ── Summary cards ── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 18px', minWidth: 120 }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#1d4ed8', letterSpacing: '0.5px' }}>TOTAL STAFF</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1d4ed8' }}>{employees.length}</div>
        </div>
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 18px', minWidth: 120 }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#16a34a', letterSpacing: '0.5px' }}>ACTIVE</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#16a34a' }}>{activeCount}</div>
        </div>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 18px', minWidth: 120 }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#dc2626', letterSpacing: '0.5px' }}>INACTIVE</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#dc2626' }}>{inactiveCount}</div>
        </div>
        {totalSalary > 0 && (
          <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 10, padding: '10px 18px', minWidth: 160 }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#7c3aed', letterSpacing: '0.5px' }}>MONTHLY PAYROLL</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#7c3aed' }}>{fmt(totalSalary)}</div>
            <div style={{ fontSize: '0.7rem', color: '#a78bfa' }}>active employees</div>
          </div>
        )}
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, code, phone, designation…"
            style={{
              width: '100%', padding: '8px 10px 8px 32px', borderRadius: 8,
              border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <select value={filterDept} onChange={(e) => setFilterDept(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#475569' }}>
          <option value="">All Departments</option>
          {depts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>

        <div style={{ display: 'flex', gap: 4 }}>
          {[['active', 'Active'], ['inactive', 'Inactive'], ['all', 'All']].map(([val, label]) => (
            <button key={val} onClick={() => setFilterStatus(val)}
              style={{
                padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: '1.5px solid',
                borderColor: filterStatus === val ? '#4f46e5' : '#e2e8f0',
                background:  filterStatus === val ? '#4f46e5' : '#fff',
                color:       filterStatus === val ? '#fff'    : '#64748b',
              }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* ── Table ── */}
      {loading ? (
        <div className="loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
          <Users size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontSize: '1rem', fontWeight: 600 }}>
            {employees.length === 0 ? 'No employees yet' : 'No employees match your filters'}
          </div>
          {employees.length === 0 && (
            <div style={{ marginTop: 8, fontSize: '0.85rem' }}>
              Click <strong>Add Employee</strong> to register your first staff member.
            </div>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>NAME</th>
                <th>DESIGNATION</th>
                <th>DEPARTMENT</th>
                <th>PHONE</th>
                <th>EMP CODE</th>
                <th>SALARY</th>
                <th>JOINED</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((emp, i) => (
                <tr key={emp.employee_id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{i + 1}</td>
                  <td>
                    <div style={{ fontWeight: 700, color: '#1e293b' }}>{emp.full_name}</div>
                    {emp.address && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {emp.address}
                      </div>
                    )}
                  </td>
                  <td>
                    {emp.designation ? (
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 12,
                        fontSize: '0.75rem', fontWeight: 600,
                        background: '#eff6ff', color: '#1d4ed8',
                      }}>{emp.designation}</span>
                    ) : '—'}
                  </td>
                  <td style={{ color: '#475569', fontSize: '0.85rem' }}>{emp.department || '—'}</td>
                  <td>
                    {emp.phone_number ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.82rem', color: '#334155', fontFamily: 'monospace' }}>
                        <Phone size={11} style={{ color: '#94a3b8' }} />{emp.phone_number}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: '#6366f1', fontWeight: 600 }}>
                    {emp.emp_code || '—'}
                  </td>
                  <td>
                    {emp.salary != null ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontWeight: 700, color: '#0f766e' }}>
                        <IndianRupee size={11} />{Number(emp.salary).toLocaleString('en-IN')}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ color: '#64748b', fontSize: '0.82rem' }}>{fmtDate(emp.join_date)}</td>
                  <td><StatusBadge active={!!emp.is_active} /></td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => openEdit(emp)}
                        className="btn btn-sm btn-secondary"
                        title="Edit employee"
                        style={{ padding: '4px 8px' }}>
                        <Edit2 size={13} />
                      </button>
                      <button
                        onClick={() => handleDelete(emp)}
                        className="btn btn-sm btn-danger"
                        title="Delete employee"
                        style={{ padding: '4px 8px' }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Footer count */}
          <div style={{ padding: '10px 16px', fontSize: '0.8rem', color: '#94a3b8', borderTop: '1px solid #f1f5f9' }}>
            Showing {filtered.length} of {employees.length} employee{employees.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {modalOpen && (
        <EmployeeModal
          employee={editTarget}
          onClose={closeModal}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

// ── Employee Attendance Panel ──────────────────────────────────────────────────
const EMP_STATUS_COLORS = { present: '#16a34a', absent: '#ef4444', half_day: '#f59e0b', leave: '#6366f1' };
const EMP_STATUS_LABELS = { present: '✅ Present', absent: '❌ Absent', half_day: '🕐 Half Day', leave: '🌴 Leave' };
const EMP_STATUS_BG     = { present: '#dcfce7', absent: '#fee2e2', half_day: '#fef3c7', leave: '#e0e7ff' };

function EmployeeAttendancePanel() {
  const [selectedDate, setSelectedDate]   = useState(new Date().toISOString().split('T')[0]);
  const [employees, setEmployees]         = useState([]);
  const [loading, setLoading]             = useState(false);
  const [saving, setSaving]               = useState(false);
  const [saved, setSaved]                 = useState(false);
  const [locked, setLocked]               = useState(false);
  const [markedDates, setMarkedDates]     = useState([]);
  const [viewMode, setViewMode]           = useState('mark'); // 'mark' | 'summary'
  const [summary, setSummary]             = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryMonth, setSummaryMonth]   = useState(''); // '' = all-time, '1'-'12' = specific month
  // in/out time state: { [employeeId]: { in_time, out_time } }
  const [timings, setTimings]             = useState({});
  const [expandedRows, setExpandedRows]   = useState(new Set());

  const updateTiming = (employeeId, field, value) => {
    if (locked) return;
    setTimings(prev => ({ ...prev, [employeeId]: { ...(prev[employeeId] || {}), [field]: value } }));
    setSaved(false);
  };

  const toggleExpand = (employeeId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId); else next.add(employeeId);
      return next;
    });
  };

  // Load employees + attendance when date changes
  useEffect(() => {
    if (!selectedDate || viewMode !== 'mark') return;
    setLoading(true);
    setSaved(false);
    setEmployees([]);
    setTimings({});
    setExpandedRows(new Set());
    employeeAttendanceAPI.get({ date: selectedDate })
      .then(res => {
        const list = res.data.data?.employees || [];
        setEmployees(list);
        const t = {};
        list.forEach(e => {
          if (e.in_time || e.out_time) {
            t[e.employee_id] = { in_time: e.in_time || '', out_time: e.out_time || '' };
          }
        });
        setTimings(t);
        setExpandedRows(new Set(Object.keys(t).map(Number)));
        // If any employee already has attendance marked for this date, freeze the table
        setLocked(list.some(e => e.status));
      })
      .catch((err) => { console.error('Employee attendance load error:', err.response?.data || err.message); setEmployees([]); })
      .finally(() => setLoading(false));
  }, [selectedDate, viewMode]);

  // Load marked dates
  useEffect(() => {
    const month = parseInt(selectedDate.split('-')[1], 10);
    employeeAttendanceAPI.getDates({ month })
      .then(res => setMarkedDates(res.data.data || []))
      .catch(() => setMarkedDates([]));
  }, [selectedDate]);

  // Load summary
  useEffect(() => {
    if (viewMode !== 'summary') return;
    setSummaryLoading(true);
    const params = {};
    if (summaryMonth) params.month = summaryMonth;
    employeeAttendanceAPI.getSummary(params)
      .then(res => setSummary(res.data.data || []))
      .catch(() => setSummary([]))
      .finally(() => setSummaryLoading(false));
  }, [viewMode, summaryMonth]);

  const updateStatus = (employeeId, status) => {
    if (locked) return;
    // Also set am/pm to match the quick-status choice so backend gets AM/PM split
    const amPmMap = {
      present:  { am_status: 'present', pm_status: 'present' },
      absent:   { am_status: 'absent',  pm_status: 'absent'  },
      leave:    { am_status: 'leave',   pm_status: 'leave'   },
      half_day: { am_status: 'present', pm_status: 'absent'  },
    };
    const extras = amPmMap[status] || {};
    setEmployees(prev => prev.map(e => e.employee_id === employeeId ? { ...e, status, ...extras } : e));
    setSaved(false);
  };

  const updateHalf = (employeeId, half, value) => {
    if (locked) return;
    setEmployees(prev => prev.map(e => {
      if (e.employee_id !== employeeId) return e;
      const am_status = half === 'am' ? value : (e.am_status || 'present');
      const pm_status = half === 'pm' ? value : (e.pm_status || 'present');
      // Derive combined status for UI/legacy
      let status;
      if (am_status === pm_status) status = am_status;
      else status = 'half_day';
      return { ...e, am_status, pm_status, status };
    }));
    setSaved(false);
  };

  const markAllPresent = () => {
    if (locked) return;
    setEmployees(prev => prev.map(e => ({ ...e, status: 'present', am_status: 'present', pm_status: 'present' })));
    setSaved(false);
  };

  const handleSave = async () => {
    for (const e of employees) {
      const t = timings[e.employee_id] || {};
      if (t.in_time && t.out_time && t.out_time <= t.in_time) {
        alert(`Invalid time for ${e.full_name}: Out time must be later than In time.`);
        return;
      }
    }
    const records = employees.filter(e => e.status).map(e => ({
      employee_id: e.employee_id,
      status:      e.status,
      am_status:   e.am_status || null,
      pm_status:   e.pm_status || null,
      in_time:     timings[e.employee_id]?.in_time  || null,
      out_time:    timings[e.employee_id]?.out_time || null,
    }));
    if (!records.length) { alert('Please mark attendance for at least one employee'); return; }
    setSaving(true);
    try {
      await employeeAttendanceAPI.save({ date: selectedDate, records });
      setSaved(true);
      setLocked(true);
      const month = parseInt(selectedDate.split('-')[1], 10);
      employeeAttendanceAPI.getDates({ month })
        .then(res => setMarkedDates(res.data.data || [])).catch(() => {});
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.message || err.message));
    }
    setSaving(false);
  };

  const isMarked = markedDates.includes(selectedDate);

  const presentCount = employees.filter(e => e.status === 'present').length;
  const absentCount  = employees.filter(e => e.status === 'absent').length;
  const halfCount    = employees.filter(e => e.status === 'half_day').length;
  const leaveCount   = employees.filter(e => e.status === 'leave').length;
  const unmarked     = employees.filter(e => !e.status).length;

  return (
    <div className="page-content">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>📋 Employee Attendance</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className={`btn ${viewMode === 'mark' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('mark')}
          >Mark Attendance</button>
          <button
            className={`btn ${viewMode === 'summary' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('summary')}
          >View Summary</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {viewMode === 'mark' && (
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            lang="en-GB"
            style={{ padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          />
        )}
        {viewMode === 'mark' && isMarked && (
          <span style={{ background: '#dcfce7', color: '#16a34a', padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
            ✅ Attendance marked for this date
          </span>
        )}
      </div>

      {viewMode === 'mark' ? (
        <>
          {/* Summary bar */}
          {employees.length > 0 && (
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
              <span style={{ background: '#f0fdf4', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                Total: {employees.length}
              </span>
              <span style={{ background: '#dcfce7', color: '#16a34a', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                Present: {presentCount}
              </span>
              <span style={{ background: '#fee2e2', color: '#ef4444', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                Absent: {absentCount}
              </span>
              <span style={{ background: '#fef3c7', color: '#d97706', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                Half Day: {halfCount}
              </span>
              <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                Leave: {leaveCount}
              </span>
              {unmarked > 0 && (
                <span style={{ background: '#f1f5f9', color: '#64748b', padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                  Unmarked: {unmarked}
                </span>
              )}
            </div>
          )}

          {loading ? (
            <div className="loading">Loading employees...</div>
          ) : employees.length === 0 ? (
            <div className="empty-state"><p>No active employees found. Add employees first from the Employee List tab.</p></div>
          ) : (
            <>
              <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="btn btn-success btn-sm" onClick={markAllPresent} disabled={locked} style={locked ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>✅ Mark All Present</button>
                {locked && (
                  <>
                    <span style={{ fontSize: 12, color: '#64748b' }}>🔒 Attendance is saved and locked</span>
                    <button className="btn btn-sm btn-secondary" onClick={() => setLocked(false)} title="Edit Attendance">✏️ Edit Attendance</button>
                  </>
                )}
              </div>
              <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Name</th>
                    <th>Designation</th>
                    <th>Department</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th style={{ textAlign: 'center', width: 80 }}>AM/PM · Time</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((e, idx) => {
                    const t = timings[e.employee_id] || {};
                    const hasTime = t.in_time || t.out_time;
                    const isExpanded = expandedRows.has(e.employee_id);
                    return (
                      <React.Fragment key={e.employee_id}>
                        <tr style={{ background: e.status === 'absent' ? '#fff5f5' : e.status === 'half_day' ? '#fffbeb' : e.status === 'leave' ? '#f5f3ff' : '' }}>
                          <td>{idx + 1}</td>
                          <td style={{ fontWeight: 500 }}>{e.full_name}</td>
                          <td>{e.designation || '—'}</td>
                          <td>{e.department || '—'}</td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
                              {['present', 'absent', 'half_day', 'leave'].map(st => (
                                <button
                                  key={st}
                                  onClick={() => updateStatus(e.employee_id, st)}
                                  disabled={locked}
                                  style={{
                                    padding: '4px 10px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: locked ? 'not-allowed' : 'pointer',
                                    border: e.status === st ? `2px solid ${EMP_STATUS_COLORS[st]}` : '1px solid #d1d5db',
                                    background: e.status === st ? EMP_STATUS_BG[st] : '#fff',
                                    color: e.status === st ? EMP_STATUS_COLORS[st] : '#64748b',
                                    opacity: locked ? 0.7 : 1
                                  }}
                                >
                                  {EMP_STATUS_LABELS[st]}
                                </button>
                              ))}
                            </div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              onClick={() => toggleExpand(e.employee_id)}
                              title="AM/PM split & In/Out time"
                              style={{
                                padding: '4px 8px', fontSize: 13, borderRadius: 6, cursor: 'pointer', border: 'none',
                                background: hasTime ? '#dbeafe' : '#f1f5f9',
                                color: hasTime ? '#1d4ed8' : '#64748b', fontWeight: hasTime ? 700 : 400
                              }}
                            >
                              ⚙{hasTime ? ' ✓' : ''}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr style={{ background: '#f8faff' }}>
                            <td colSpan={6} style={{ padding: '10px 16px 14px' }}>
                              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <div>
                                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 3 }}>MORNING (AM)</label>
                                  <select
                                    value={e.am_status || 'present'}
                                    disabled={locked}
                                    onChange={ev => updateHalf(e.employee_id, 'am', ev.target.value)}
                                    style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: 130, background: '#fff' }}
                                  >
                                    <option value="present">✅ Present</option>
                                    <option value="absent">❌ Absent</option>
                                    <option value="leave">🌴 Leave</option>
                                  </select>
                                </div>
                                <div>
                                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 3 }}>AFTERNOON (PM)</label>
                                  <select
                                    value={e.pm_status || 'present'}
                                    disabled={locked}
                                    onChange={ev => updateHalf(e.employee_id, 'pm', ev.target.value)}
                                    style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: 130, background: '#fff' }}
                                  >
                                    <option value="present">✅ Present</option>
                                    <option value="absent">❌ Absent</option>
                                    <option value="leave">🌴 Leave</option>
                                  </select>
                                </div>
                                <div style={{ width: 1, alignSelf: 'stretch', background: '#e2e8f0', margin: '0 4px' }}></div>
                                <div>
                                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 3 }}>IN TIME</label>
                                  <input
                                    type="time"
                                    value={t.in_time || ''}
                                    disabled={locked}
                                    onChange={ev => updateTiming(e.employee_id, 'in_time', ev.target.value)}
                                    style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: 120 }}
                                  />
                                </div>
                                <div>
                                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 3 }}>OUT TIME</label>
                                  <input
                                    type="time"
                                    value={t.out_time || ''}
                                    disabled={locked}
                                    onChange={ev => {
                                      const outVal = ev.target.value;
                                      if (t.in_time && outVal && outVal <= t.in_time) {
                                        alert('Out time must be later than In time.');
                                        return;
                                      }
                                      updateTiming(e.employee_id, 'out_time', outVal);
                                    }}
                                    style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: 120 }}
                                  />
                                  {t.in_time && t.out_time && t.out_time <= t.in_time && (
                                    <div style={{ color: '#dc2626', fontSize: 11, marginTop: 2 }}>Must be after in time</div>
                                  )}
                                </div>
                                {hasTime && !locked && (
                                  <button
                                    onClick={() => { setTimings(prev => { const n = { ...prev }; delete n[e.employee_id]; return n; }); setSaved(false); }}
                                    style={{ padding: '5px 10px', fontSize: 12, border: '1px solid #fca5a5', borderRadius: 6, background: '#fff5f5', color: '#dc2626', cursor: 'pointer' }}
                                  >✕ Clear Time</button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                {saved && <span style={{ color: '#16a34a', fontWeight: 600, alignSelf: 'center' }}>✅ Saved successfully!</span>}
                {!locked && (
                  <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : '💾 Save Attendance'}
                  </button>
                )}
              </div>
            </>
          )}
        </>
      ) : (
        /* Summary View */
        <>
          <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>Filter by Month:</label>
            <select
              value={summaryMonth}
              onChange={e => setSummaryMonth(e.target.value)}
              style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
            >
              <option value="">All Months (Full Year)</option>
              {['January','February','March','April','May','June','July','August','September','October','November','December']
                .map((m, i) => <option key={i+1} value={String(i+1)}>{m}</option>)}
            </select>
            {summaryMonth && (
              <span style={{ fontSize: 12, color: '#64748b' }}>
                Showing data for {['January','February','March','April','May','June','July','August','September','October','November','December'][parseInt(summaryMonth)-1]} only
              </span>
            )}
          </div>
          {summaryLoading ? (
            <div className="loading">Loading summary...</div>
          ) : summary.length === 0 ? (
            <div className="empty-state"><p>No attendance data found yet.</p></div>
          ) : (
            <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Name</th>
                  <th>Designation</th>
                  <th style={{ textAlign: 'center' }}>Total Days</th>
                  <th style={{ textAlign: 'center' }}>Present</th>
                  <th style={{ textAlign: 'center' }}>Absent</th>
                  <th style={{ textAlign: 'center' }}>Half Day</th>
                  <th style={{ textAlign: 'center' }}>Leave</th>
                  <th style={{ textAlign: 'center' }}>Attendance %</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((s, idx) => {
                  const pct = s.total_days > 0 ? Math.round(((s.present_days + s.half_days * 0.5) / s.total_days) * 100) : 0;
                  return (
                    <tr key={s.employee_id}>
                      <td>{idx + 1}</td>
                      <td style={{ fontWeight: 500 }}>{s.full_name}</td>
                      <td>{s.designation || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{s.total_days}</td>
                      <td style={{ textAlign: 'center', color: '#16a34a', fontWeight: 600 }}>{s.present_days}</td>
                      <td style={{ textAlign: 'center', color: '#ef4444', fontWeight: 600 }}>{s.absent_days}</td>
                      <td style={{ textAlign: 'center', color: '#d97706', fontWeight: 600 }}>{s.half_days}</td>
                      <td style={{ textAlign: 'center', color: '#4338ca', fontWeight: 600 }}>{s.leave_days}</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: pct >= 90 ? '#16a34a' : pct >= 75 ? '#d97706' : '#ef4444' }}>{pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Salary Panel (auto-calculated from attendance) ──────────────────────────
function EmployeeSalaryPanel() {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(defaultMonth);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ working_days: 0, cl_quota_per_year: 12, frozen: false });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [cfg, setCfg] = useState({ cl_quota_per_year: 12, working_day_rule: 'exclude_sundays' });
  const [showCfg, setShowCfg] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [freezing, setFreezing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [monRes, cfgRes] = await Promise.all([
        salaryAPI.getMonthly(month),
        salaryAPI.getConfig(),
      ]);
      const d = monRes.data.data;
      setRows(d.rows || []);
      setMeta({ working_days: d.working_days, cl_quota_per_year: d.cl_quota_per_year, frozen: d.frozen });
      setCfg(cfgRes.data.data);
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to load salary data');
      setRows([]);
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      await salaryAPI.setConfig(cfg);
      setShowCfg(false);
      load();
    } catch (e) {
      alert('Failed to save config: ' + (e.response?.data?.message || e.message));
    } finally { setSavingCfg(false); }
  };

  const freeze = async () => {
    if (!window.confirm(`Freeze salary snapshot for ${month}?\n\nAfter freezing, changes to attendance won't affect this month's payroll unless you unfreeze.`)) return;
    setFreezing(true);
    try {
      await salaryAPI.freeze(month);
      load();
    } catch (e) {
      alert('Failed to freeze: ' + (e.response?.data?.message || e.message));
    } finally { setFreezing(false); }
  };

  const unfreeze = async () => {
    if (!window.confirm(`Unfreeze ${month}? Salary will be live-computed from attendance again.`)) return;
    setFreezing(true);
    try {
      await salaryAPI.unfreeze(month);
      load();
    } catch (e) {
      alert('Failed to unfreeze: ' + (e.response?.data?.message || e.message));
    } finally { setFreezing(false); }
  };

  const downloadCSV = () => {
    const header = ['Name','Designation','Department','Monthly Salary','Working Days','Present','Absent','CL Paid','CL Unpaid','Per Day','Deduction','Net Payable'];
    const csvRows = [header.join(',')];
    for (const r of rows) {
      csvRows.push([
        `"${(r.full_name || '').replace(/"/g, '""')}"`,
        `"${(r.designation || '').replace(/"/g, '""')}"`,
        `"${(r.department || '').replace(/"/g, '""')}"`,
        r.monthly_salary, r.working_days, r.present_days, r.absent_days,
        r.leave_paid_days, r.leave_unpaid_days, r.per_day_rate, r.deduction, r.net_payable,
      ].join(','));
    }
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `salary-${month}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printReport = () => {
    const win = window.open('', '_blank');
    if (!win) { alert('Popup blocked. Allow popups to print.'); return; }
    const monthLabel = new Date(month + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    const totalNet = rows.reduce((s, r) => s + Number(r.net_payable || 0), 0);
    const totalDed = rows.reduce((s, r) => s + Number(r.deduction   || 0), 0);
    const totalMon = rows.reduce((s, r) => s + Number(r.monthly_salary || 0), 0);
    const fmtInr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
    const rowsHtml = rows.map((r, i) => `
      <tr>
        <td>${i+1}</td>
        <td><strong>${r.full_name}</strong><br/><small>${r.designation || ''}</small></td>
        <td style="text-align:right">${fmtInr(r.monthly_salary)}</td>
        <td style="text-align:center">${r.working_days}</td>
        <td style="text-align:center">${r.present_days}</td>
        <td style="text-align:center">${r.absent_days}</td>
        <td style="text-align:center">${r.leave_paid_days}</td>
        <td style="text-align:center">${r.leave_unpaid_days}</td>
        <td style="text-align:right">${fmtInr(r.per_day_rate)}</td>
        <td style="text-align:right;color:#dc2626">${fmtInr(r.deduction)}</td>
        <td style="text-align:right;font-weight:700;color:#16a34a">${fmtInr(r.net_payable)}</td>
      </tr>
    `).join('');
    win.document.write(`
      <html><head><title>Salary Report – ${monthLabel}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#1e293b}
        h1{margin:0 0 4px}
        .meta{color:#64748b;font-size:13px;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th,td{border:1px solid #cbd5e1;padding:6px 8px}
        th{background:#f1f5f9;text-align:left;font-size:11px;letter-spacing:.5px}
        tfoot td{font-weight:700;background:#f8fafc}
        @media print{ button{display:none} }
      </style></head><body>
      <button onclick="window.print()" style="float:right;padding:8px 14px;cursor:pointer">🖨 Print</button>
      <h1>Salary Report</h1>
      <div class="meta">
        Month: <b>${monthLabel}</b> &nbsp;|&nbsp;
        Working Days: <b>${meta.working_days}</b> (Sundays excluded) &nbsp;|&nbsp;
        CL Quota: <b>${meta.cl_quota_per_year}/year</b>
        ${meta.frozen ? ' &nbsp;|&nbsp; <span style="color:#16a34a">✅ Frozen snapshot</span>' : ''}
      </div>
      <table>
        <thead><tr>
          <th>#</th><th>Employee</th><th>Salary</th><th>WD</th><th>Present</th>
          <th>Absent</th><th>CL Paid</th><th>CL Unpaid</th><th>Per Day</th>
          <th>Deduction</th><th>Net Payable</th>
        </tr></thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot><tr>
          <td colspan="2">TOTAL (${rows.length})</td>
          <td style="text-align:right">${fmtInr(totalMon)}</td>
          <td colspan="6"></td>
          <td style="text-align:right;color:#dc2626">${fmtInr(totalDed)}</td>
          <td style="text-align:right;color:#16a34a">${fmtInr(totalNet)}</td>
        </tr></tfoot>
      </table>
      </body></html>
    `);
    win.document.close();
  };

  const totalNet = rows.reduce((s, r) => s + Number(r.net_payable || 0), 0);
  const totalDed = rows.reduce((s, r) => s + Number(r.deduction   || 0), 0);

  return (
    <div className="page-content">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>💰 Auto-Calculated Salary</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowCfg(v => !v)}>⚙ Rules</button>
          <button className="btn btn-secondary btn-sm" onClick={downloadCSV} disabled={!rows.length}>⬇ CSV</button>
          <button className="btn btn-secondary btn-sm" onClick={printReport} disabled={!rows.length}>🖨 Print</button>
          {meta.frozen
            ? <button className="btn btn-sm" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }} onClick={unfreeze} disabled={freezing}>🔓 Unfreeze</button>
            : <button className="btn btn-primary btn-sm" onClick={freeze} disabled={freezing || !rows.length}>❄ Freeze Month</button>
          }
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontWeight: 600, fontSize: 13, color: '#374151' }}>Month:</label>
        <input
          type="month"
          value={month}
          onChange={e => setMonth(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
        />
        <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600 }}>
          Working Days: {meta.working_days}
        </span>
        <span style={{ background: '#f0fdf4', color: '#16a34a', padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600 }}>
          CL Quota: {meta.cl_quota_per_year}/year
        </span>
        {meta.frozen && (
          <span style={{ background: '#fef3c7', color: '#92400e', padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>
            ❄ Snapshot Frozen
          </span>
        )}
      </div>

      {/* Rules panel */}
      {showCfg && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 16, maxWidth: 640 }}>
          <div style={{ fontWeight: 700, marginBottom: 12, color: '#1e293b' }}>Salary Calculation Rules</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                Casual Leave Quota (per year)
              </label>
              <input
                type="number" min="0" max="60"
                value={cfg.cl_quota_per_year}
                onChange={e => setCfg({ ...cfg, cl_quota_per_year: parseInt(e.target.value, 10) || 0 })}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}
              />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Leaves within quota = paid; beyond = unpaid</div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                Working Days Rule
              </label>
              <select
                value={cfg.working_day_rule}
                onChange={e => setCfg({ ...cfg, working_day_rule: e.target.value })}
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}
              >
                <option value="exclude_sundays">Calendar days minus Sundays</option>
                <option value="all_days">All calendar days</option>
              </select>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Per-day rate = salary ÷ working days</div>
            </div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowCfg(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={saveConfig} disabled={savingCfg}>
              {savingCfg ? 'Saving…' : 'Save Rules'}
            </button>
          </div>
        </div>
      )}

      {err && <div className="error-banner">{err}</div>}

      {loading ? (
        <div className="loading">Computing salaries…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p>No active employees found, or none have a monthly salary configured.</p>
          <p style={{ fontSize: 12, color: '#94a3b8' }}>Go to <strong>Employee List</strong> and set the Monthly Salary for each employee.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Name</th>
                <th style={{ textAlign: 'right' }}>Monthly Salary</th>
                <th style={{ textAlign: 'center' }}>Present</th>
                <th style={{ textAlign: 'center' }}>Absent</th>
                <th style={{ textAlign: 'center' }}>CL Paid</th>
                <th style={{ textAlign: 'center' }}>CL Unpaid</th>
                <th style={{ textAlign: 'right' }}>Per Day</th>
                <th style={{ textAlign: 'right' }}>Deduction</th>
                <th style={{ textAlign: 'right' }}>Net Payable</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.employee_id}>
                  <td>{i + 1}</td>
                  <td>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{r.full_name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{r.designation || '—'}</div>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(r.monthly_salary)}</td>
                  <td style={{ textAlign: 'center', color: '#16a34a', fontWeight: 600 }}>{r.present_days}</td>
                  <td style={{ textAlign: 'center', color: '#ef4444', fontWeight: 600 }}>{r.absent_days}</td>
                  <td style={{ textAlign: 'center', color: '#4338ca', fontWeight: 600 }}>{r.leave_paid_days}</td>
                  <td style={{ textAlign: 'center', color: '#dc2626', fontWeight: 600 }}>{r.leave_unpaid_days}</td>
                  <td style={{ textAlign: 'right', fontSize: 12, color: '#64748b' }}>{fmt(r.per_day_rate)}</td>
                  <td style={{ textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>-{fmt(r.deduction)}</td>
                  <td style={{ textAlign: 'right', color: '#16a34a', fontWeight: 800 }}>{fmt(r.net_payable)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#f8fafc', fontWeight: 700 }}>
                <td colSpan={8} style={{ textAlign: 'right' }}>TOTAL ({rows.length} employees)</td>
                <td style={{ textAlign: 'right', color: '#dc2626' }}>-{fmt(totalDed)}</td>
                <td style={{ textAlign: 'right', color: '#16a34a' }}>{fmt(totalNet)}</td>
              </tr>
            </tfoot>
          </table>
          </div>

          <div style={{ marginTop: 14, padding: 12, background: '#eff6ff', borderRadius: 8, fontSize: 12, color: '#1e40af' }}>
            <strong>How it's calculated:</strong> Per-day rate = Monthly Salary ÷ Working Days ({meta.working_days}).
            Absent + Unpaid Leave days are deducted. Casual Leaves within the yearly quota ({meta.cl_quota_per_year}) are paid.
            Half-day (AM or PM only) counts as 0.5 day. Change these rules using the ⚙ Rules button.
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Employees Page (tab wrapper) ─────────────────────────────────────────
export default function Employees() {
  const [subTab, setSubTab] = useState('list'); // 'list' | 'attendance' | 'salary'

  return (
    <div className="page">
      <div className="tabs">
        <button className={`tab${subTab === 'list'       ? ' active' : ''}`} onClick={() => setSubTab('list')}>👥 Employee List</button>
        <button className={`tab${subTab === 'attendance' ? ' active' : ''}`} onClick={() => setSubTab('attendance')}>📋 Attendance</button>
        <button className={`tab${subTab === 'salary'     ? ' active' : ''}`} onClick={() => setSubTab('salary')}>💰 Salary</button>
      </div>
      {subTab === 'list'       && <EmployeeListPanel />}
      {subTab === 'attendance' && <EmployeeAttendancePanel />}
      {subTab === 'salary'     && <EmployeeSalaryPanel />}
    </div>
  );
}
